
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "convex/react";
import { api } from "../../api";
import { useAppStoreLookup } from "./useAppStoreLookup";

interface BulkAppImportResult {
  bundleId: string;
  appName?: string;
  status: 'success' | 'error' | 'skipped';
  message: string;
}

interface BulkAppImportProgress {
  processed: number;
  total: number;
}

interface AppToImport {
  bundleId: string;
  suggestedName: string | null;
}

export const useBulkAppImport = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<BulkAppImportProgress>({ processed: 0, total: 0 });
  const [results, setResults] = useState<BulkAppImportResult[]>([]);
  const { toast } = useToast();
  const { lookupByBundleId } = useAppStoreLookup();
  const addApp = useMutation(api.apps.add.app);

  const importApps = async (appsToImport: AppToImport[]) => {
    setIsImporting(true);
    setProgress({ processed: 0, total: appsToImport.length });
    setResults([]);

    try {
      const importResults: BulkAppImportResult[] = [];

      for (let i = 0; i < appsToImport.length; i++) {
        const app = appsToImport[i];
        
        try {
          console.log('Looking up app:', app.bundleId);
          
          // Lookup app info from iTunes
          const lookupResult = await lookupByBundleId(app.bundleId);
          
          let appName = app.suggestedName || app.bundleId;
          let category = "Unknown";
          let iconUrl = "";
          let trackId = "";
          
          if (lookupResult.found && lookupResult.app) {
            appName = lookupResult.app.appName;
            category = lookupResult.app.category;
            iconUrl = lookupResult.app.iconUrl || "";
            trackId = lookupResult.app.trackId.toString();
          } else {
            // System app or not found - use defaults
            category = "System";
          }
          
          // Add app to database
          await addApp({
            name: appName,
            bundleId: app.bundleId,
            iconUrl,
            category,
            trackId,
            tags: []
          });
          
          const result: BulkAppImportResult = {
            bundleId: app.bundleId,
            appName,
            status: 'success',
            message: lookupResult.found ? 'Successfully imported from App Store' : 'Successfully imported as system app'
          };
          
          importResults.push(result);
        } catch (error) {
          console.error('Failed to import app:', app.bundleId, error);
          const result: BulkAppImportResult = {
            bundleId: app.bundleId,
            appName: app.suggestedName || app.bundleId,
            status: 'error',
            message: error instanceof Error ? error.message : 'Failed to import'
          };
          importResults.push(result);
        }
        
        setResults([...importResults]);
        setProgress({ processed: i + 1, total: appsToImport.length });
      }
      
      toast({
        title: "Import Complete",
        description: `Successfully processed ${appsToImport.length} apps`,
      });
    } catch (error) {
      console.error('Bulk import failed:', error);
      toast({
        title: "Import Failed",
        description: "Failed to import apps",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsImporting(false);
    }
  };

  const reset = () => {
    setProgress({ processed: 0, total: 0 });
    setResults([]);
    setIsImporting(false);
  };

  return {
    importApps,
    isImporting,
    progress,
    results,
    reset
  };
};
